import { parseLocaleNumber } from "@/lib/businessRules";

export const MONTH_NAMES = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
export const MONTHLY_FIELDS = [
  {key: "annual_revenue_target", label: "CA net du mois", unit: "€"},
  {key: "fixed_costs_target", label: "Charges fixes du mois", unit: "€"},
  {key: "forecast_result_target", label: "Résultat net du mois", unit: "€", signed: true},
  {key: "total_annual_billed_etp", label: "ETP facturés du mois", hint: "Base 18 jours facturés par ETP mensuel ; l’annuel reste en base 210 jours."},
  {key: "tjm_target", label: "TJM net moyen", unit: "€"},
  {key: "margin_target", label: "Marge cible", unit: "%", percent: true},
  {key: "total_resources_end_year", label: "Effectif total en fin de mois"},
  {key: "salaried_resources_end_year", label: "Salariés en fin de mois"},
  {key: "external_resources_end_year", label: "Externes en fin de mois"},
  {key: "entries_per_month", label: "Entrées du mois"},
  {key: "avg_new_billed_etp", label: "Nouveaux ETP facturés du mois"},
];
export const VISIBLE_MONTHLY_FIELDS = MONTHLY_FIELDS.filter(field => !["salaried_resources_end_year", "external_resources_end_year", "avg_new_billed_etp"].includes(field.key));
export const COMMON_MONTHLY_FIELDS = MONTHLY_FIELDS.filter(field => ["tjm_target", "margin_target"].includes(field.key));
export const commonMonthlyDraft = objective => Object.fromEntries(COMMON_MONTHLY_FIELDS.map(field => {
  const stored = objective?.monthly_defaults || {};
  const value = Object.prototype.hasOwnProperty.call(stored, field.key) ? stored[field.key] : (objective?.annual_targets || objective?.targets || {})[field.key];
  return [field.key, value == null ? "" : String(value * (field.percent ? 100 : 1))];
}));
export const parseCommonMonthlyDraft = draft => Object.fromEntries(COMMON_MONTHLY_FIELDS.map(field => [field.key, String(draft[field.key] ?? "").trim() ? parseMonthlyDraft({"01": {[field.key]: draft[field.key]}})["01"][field.key] : null]));
export const RULE_LABELS = {SUM: "Somme des 12 mois", BILLED_DAYS: "Jours facturés / 210", AVERAGE: "Moyenne des 12 mois", PERIOD_END: "Valeur de décembre", WEIGHTED_DAYS: "Moyenne pondérée par les ETP", WEIGHTED_REVENUE: "Marge pondérée par le CA"};
export const PLAN_LABELS = {MISMATCH: "Écart annuel / mensuel", INCOMPLETE: "Mois à compléter", MATCH: "Annuel et mensuel cohérents", ANNUAL_ONLY: "Saisie annuelle uniquement"};
export const planTone = status => status === "MISMATCH" ? "border-red-400/40 bg-red-400/10 text-red-200" : status === "INCOMPLETE" ? "border-amber-400/30 bg-amber-400/10 text-amber-200" : status === "MATCH" ? "border-emerald-400/30 bg-emerald-400/10 text-emerald-200" : "border-white/10 bg-white/[0.03] text-slate-300";
export const formatMonthlyValue = (value, field) => {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return "—";
  const number = Number(value) * (field.percent ? 100 : 1);
  return `${new Intl.NumberFormat("fr-FR", {maximumFractionDigits: field.percent ? 2 : field.unit === "€" ? 2 : 3}).format(number)}${field.unit ? ` ${field.unit}` : ""}`;
};
export const monthlyDraft = monthly => Object.fromEntries(Object.entries(monthly || {}).map(([month, values]) => [month, Object.fromEntries(MONTHLY_FIELDS.map(field => [field.key, values[field.key] === null || values[field.key] === undefined ? "" : String(values[field.key] * (field.percent ? 100 : 1))]))]));
export const parseMonthlyDraft = draft => {
  const result = {};
  for (const [month, values] of Object.entries(draft)) {
    const parsed = {};
    for (const field of MONTHLY_FIELDS) {
      const raw = String(values[field.key] ?? "").trim();
      if (!raw) continue;
      const normalized = raw.replace(/[−–﹣－]/g, "-").replace(/[\s\u202f\u00a0]/g, "").replace(/,/g, ".");
      const value = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/.test(normalized) ? parseLocaleNumber(normalized) : NaN;
      if (!Number.isFinite(value) || (!field.signed && value < 0) || (field.percent && value > 100)) throw new Error(`${MONTH_NAMES[Number(month) - 1]} : ${field.label} doit contenir un nombre valide${field.percent ? " entre 0 et 100" : ""}.`);
      parsed[field.key] = field.percent ? value / 100 : value;
    }
    if (Object.keys(parsed).length) result[month] = parsed;
  }
  return result;
};

// Explicit draft-only action: never replaces a populated cell (including zero).
export const fillEmptyMonthsFrom = (draft, sourceMonth) => {
  const source = draft[sourceMonth] || {};
  return Object.fromEntries(MONTH_NAMES.map((_, index) => {
    const month = String(index + 1).padStart(2, "0");
    const values = {...draft[month]};
    for (const field of MONTHLY_FIELDS) {
      if (String(values[field.key] ?? "").trim() === "" && String(source[field.key] ?? "").trim() !== "") values[field.key] = source[field.key];
    }
    return [month, values];
  }));
};
